/**
 * Modal upgrade adapter — bring a FOREIGN .modal onto the unified stack in place.
 *
 * The genuinely template-less modal families (eModal .alert/.confirm/.ajax,
 * jtl.article.js quickView, and any parent shell with no reachable template) have
 * no Smarty markup to override — their shell is built at runtime from a JS string.
 * This adapter upgrades such a shell IN PLACE: it stamps `.datte-modal` + the
 * opted-in `data-datte-*` attributes onto the foreign node and hands it to the
 * EXISTING `initModalRegistry()`, so the EXISTING `ModalController` wires it with
 * the SAME engine / behaviour modules / CSS base as the theme modals (JG1/JG4).
 *
 * It is ADDITIVE + IDEMPOTENT (JG3): it never strips JTL/eModal behaviour (it only
 * adds classes/attrs/wiring), and re-running on an already-upgraded node is a
 * no-op (the `.datte-modal` guard here + the registry's INITIALISED_FLAG). It does
 * NOT re-implement open/close/focus/history — those stay in the reused controller;
 * for eModal the `ModalManager` keeps owning open/close, the adapter only decorates.
 *
 * @see docs/architecture/modals/jtl-integration-architecture.md §5
 */

import type { Unbind } from '../utils/unbind';
import { initModalRegistry } from './modal-registry';

const NOOP: Unbind = () => {};

export interface UpgradeOptions {
  /** Extra root class for per-modal CSS hooks, e.g. 'datte-emodal'. */
  variant?: string;
  /** Android-back-closes. Default false — transient eModal alerts/confirms must
   *  NOT consume a back-press (spec §N item 6); content modals (C3/C4) opt in. */
  historyBack?: boolean;
  /** Focus-trap + restore. Default true. */
  focusTrap?: boolean;
  /** Mobile full-page sheet (pure-CSS hook). Default true. */
  fullscreenMobile?: boolean;
  /** Body is long → keep scroll inside the dialog (.modal-dialog-scrollable). */
  scrollable?: boolean;
  /** Also tag legacy `.close` / `.close-btn` buttons as `.datte-modal-close` so the
   *  base styles them. Rarely needed — the CSS base styles real dismiss controls
   *  (`[data-dismiss="modal"]` / `.datte-modal-close`) inside `.datte-modal`
   *  directly (§6 item 6), and every observed foreign close button carries
   *  `data-dismiss="modal"` — but available for a shell whose close button lacks
   *  the dismiss attribute and needs the explicit class hook. */
  normaliseClose?: boolean;
}

/**
 * Compute the `data-datte-close-style` for a foreign shell: `header` when the
 * shell carries a `.modal-header` (the close button lives in the header row),
 * else `corner` (an absolutely-positioned X). Pure — exported for unit testing.
 */
export function closeStyleFor(el: HTMLElement): 'header' | 'corner' {
  return el.querySelector('.modal-header') ? 'header' : 'corner';
}

/**
 * Out-of-scope foreign shells the adapter must NOT upgrade: third-party plugin
 * modals whose markup we do not own (spec §7: D2 `.kk-popup-modal` is OUT). The
 * `.consent-modal` and gallery zoom are not `.modal` at all so they never reach
 * here, but `.kk-popup-modal` IS a `.modal` and would otherwise be upgraded on
 * show — re-classing a plugin's shell risks altering its look/behaviour, so we
 * leave it pristine.
 */
const UPGRADE_EXCLUDE_SELECTOR = '.kk-popup-modal';

/**
 * Whether a node is eligible for upgrade: a `.modal` that is not already a
 * `.datte-modal` and is not an out-of-scope third-party plugin shell. Pure
 * predicate — the delegated listener uses it to skip theme-owned modals,
 * already-upgraded foreign ones (JG3), and plugin markup we do not own. Exported
 * for tests.
 */
export function shouldUpgrade(el: HTMLElement): boolean {
  return (
    el.classList.contains('modal') &&
    !el.classList.contains('datte-modal') &&
    !el.matches(UPGRADE_EXCLUDE_SELECTOR)
  );
}

/**
 * Upgrade a foreign `.modal` node in place and wire it through the existing
 * registry. Returns an `Unbind` that destroys the controller (idempotent). A
 * no-op `Unbind` is returned when the node is already a `.datte-modal` (JG3).
 */
export function upgradeForeignModal(el: HTMLElement, opts: UpgradeOptions = {}): Unbind {
  // Idempotent guard: already upgraded → do nothing, return a no-op teardown.
  if (el.classList.contains('datte-modal')) return NOOP;

  const {
    variant,
    historyBack = false,
    focusTrap = true,
    fullscreenMobile = true,
    scrollable = false,
    normaliseClose = false,
  } = opts;

  // --- additive class/attribute stamping (never remove JTL state) -------------
  el.classList.add('datte-modal');
  if (variant) {
    for (const cls of variant.split(/\s+/)) {
      if (cls) el.classList.add(cls);
    }
  }

  el.setAttribute('data-datte-close-style', closeStyleFor(el));
  if (fullscreenMobile) el.setAttribute('data-datte-fullscreen', '');
  if (focusTrap) el.setAttribute('data-datte-focus-trap', '');
  if (historyBack) el.setAttribute('data-datte-history-back', '');

  if (scrollable) {
    el.querySelector('.modal-dialog')?.classList.add('modal-dialog-scrollable');
  }

  if (normaliseClose) {
    el.querySelectorAll<HTMLElement>('.close, .close-btn').forEach((btn) => {
      btn.classList.add('datte-modal-close');
    });
  }

  // --- hand the now-.datte-modal node to the EXISTING registry ---------------
  // A scoped re-scan constructs exactly one ModalController via INITIALISED_FLAG.
  // Scope to the node's parent so the scan is cheap; fall back to document.
  const scope: ParentNode = el.parentNode ?? document;
  const controllers = initModalRegistry(scope);
  const controller = controllers.find((c) => c.modalEl === el) ?? null;

  if (!controller) return NOOP;
  return () => controller.destroy();
}
